import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import WelcomeModal from './Modals/WelcomeModal';

import ClientsAccordion from 'components/ClientsAcordion/ClientsAcordion';
import './ClientDashboard.scss';
import Search from 'components/Search/Search';
import { getIsServicePorviderExists } from 'api/clients/getIsServicePorviderExists';
import PutchEmailNotifyModal from 'components/ClientTable/Modals/NotifyModal/PutchEmailNotifyModal';
import { GroupedObject } from 'api/clients/types';
import CustomPagination from 'components/Common/PaginationDataTable';
import AddOwnerModal from 'components/ClientTable/Modals/AddOwnerModal';
import AddTechnicianModal from 'components/ClientTable/Modals/AddTechnicanModal/AddTechnicianModal';
import DeleteTechnicianModal from 'components/ClientTable/Modals/DeleteTechnicianModal/DeleteTechnicianModal';
import { useDeleteTechnicianModalStore } from 'components/ClientTable/Modals/DeleteTechnicianModal/DeleteTechnicianModalStore';

interface Props {
  data: GroupedObject[];
  refetch: () => void;
  isRefetching: boolean;
}

const ClientDashboard: React.FC<Props> = ({ data, refetch }) => {
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalPages, setTotalPages] = useState(1);
  const clientsPerPage = 7;
  const paginatedClients = data.slice((currentPage - 1) * clientsPerPage, currentPage * clientsPerPage);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [isCompanyInactive] = useState(false);
  const { t } = useTranslation();
  const { isDeleteOnClientModal } = useDeleteTechnicianModalStore();

  const handlePageChange = (page: number) => {
    if (page !== currentPage) {
      setCurrentPage(page);
    }
  };

  useEffect(() => {
    setTotalPages(Math.ceil(data.length / clientsPerPage));
  }, [data]);

  useEffect(() => {
    if (!isCompanyInactive) {
      return;
    }

    const pollServiceProvider = async () => {
      try {
        const { is_pest_company, is_active } = await getIsServicePorviderExists();

        if (is_pest_company && is_active) {
          window.location.reload();
        }
      } catch (error) {
        console.error('Polling error:', error);
      }
    };

    // Polling each 5 seconds
    const pollingInterval = setInterval(pollServiceProvider, 5000);

    return () => {
      clearInterval(pollingInterval);
    };
  }, [isCompanyInactive]);

  // Debounce function
  const debounce = (func: Function, delay: number) => {
    let timer: NodeJS.Timeout;
    return (...args: any[]) => {
      clearTimeout(timer);
      timer = setTimeout(() => {
        func(...args);
      }, delay);
    };
  };

  // Debounced search function
  const debouncedSearch = debounce((query: string) => {
    setSearchQuery(query);
    setCurrentPage(1);
  }, 500);

  // useEffect to attach event listener to search input
  useEffect(() => {
    const searchInput = document.getElementById('searchClientCompany') as HTMLInputElement;
    const handleSearch = (event: Event) => {
      const target = event.target as HTMLInputElement;
      debouncedSearch(target.value);
    };
    searchInput.addEventListener('input', handleSearch);
    return () => {
      searchInput.removeEventListener('input', handleSearch);
    };
  }, [debouncedSearch]);

  useEffect(() => {
    refetch();
  }, [refetch, currentPage, searchQuery]);

  return (
    <>
      <div className="row">
        <div className="col-xl-12 col-lg-12">
          <div className="card shadow-lg min-vh-80">
            <div className="card-header">
              <div className="d-flex align-items-center justify-content-between">
                <h4 className="card-title mb-0 fw-bold">{t('ClientsDashboard.dashboard.dashboard-title')}</h4>

                <div className="d-flex align-items-center">
                  <Search />
                </div>
              </div>
            </div>

            <div className="card-body m-0 p-0">
              <ClientsAccordion data={paginatedClients} refetchClients={refetch} />
            </div>
            <div className="card-footer">
              <div className="row">
                <div className="col-12 text-end">
                  <CustomPagination currentPage={currentPage} totalPages={totalPages} onPageChange={handlePageChange} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <PutchEmailNotifyModal refetchClients={refetch} />
      <AddTechnicianModal refetchClients={refetch} />
      {isDeleteOnClientModal && <DeleteTechnicianModal refetch={refetch} />}
      <AddOwnerModal refetchClients={refetch} />
      <WelcomeModal />
    </>
  );
};

export default ClientDashboard;
